/**
 * Owns the shared "applies to" target picker used by
 * menu-scoped forms (pricing rules, promotions).
 * Coordinates the scope radios (entire menu /
 * sections / items), a debounced search input
 * that fetches matching sections or items as a
 * turbo-stream, and the selected chips backed by
 * hidden inputs the form submits.
 *
 * Multi-select: clicking a result toggles its chip +
 * hidden input. Changing scope clears the selection
 * and re-runs the search against the new scope.
 */
import { Controller } from "@hotwired/stimulus";
import debounce from "lodash/debounce";
import { get } from "@rails/request.js";
import * as Sentry from "@sentry/browser";
import ToastService from "../../shared/toast_service";

export default class extends Controller {
  static targets = ["picker", "input", "chips", "hiddenInputs", "results"];
  static values = {
    url: String,
    scopeSelector: String,
    errorMessage: {
      type: String,
      default: "Search failed. Please try again.",
    },
  };

  connect() {
    this.search = debounce(this._run.bind(this), 250);
    this.syncMode();
  }

  scopeChanged() {
    this.clearAll();
    this.syncMode();
    if (this._needsPicker()) {
      this._run();
    }
  }

  onInput() {
    this.search();
  }

  refresh() {
    this._run();
  }

  toggle(event) {
    event.preventDefault();
    const { token, label } = event.params;
    if (!token) return;

    if (this._hasToken(token)) {
      this._removeToken(token);
    } else {
      this._addToken(token, label);
    }
    this._run();
  }

  remove(event) {
    event.preventDefault();
    const { token } = event.params;
    if (!token) return;

    this._removeToken(token);
    this._run();
  }

  clearAll() {
    this.chipsTarget.innerHTML = "";
    this.hiddenInputsTarget.innerHTML = "";
  }

  syncMode() {
    if (this._needsPicker()) {
      this.pickerTarget.classList.remove("hidden");
    } else {
      this.pickerTarget.classList.add("hidden");
      this.clearAll();
    }
  }

  _needsPicker() {
    const scope = this._scope();
    return scope === "sections" || scope === "items";
  }

  _scope() {
    const el = document.querySelector(this.scopeSelectorValue + ":checked");
    return el ? el.value : "";
  }

  _selectedTokens() {
    return Array.from(this.hiddenInputsTarget.querySelectorAll("input")).map(
      (input) => input.value,
    );
  }

  _hasToken(token) {
    return this._selectedTokens().includes(token);
  }

  _addToken(token, label) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = this.element.dataset.inputName;
    input.value = token;
    input.dataset.token = token;
    this.hiddenInputsTarget.appendChild(input);

    this.chipsTarget.appendChild(this._buildChip(token, label));
  }

  _buildChip(token, label) {
    const chip = document.createElement("span");
    chip.className = "badge badge-accent gap-1 py-3";
    chip.dataset.token = token;
    chip.dataset.testid = "menu-target-chip";

    const text = document.createElement("span");
    text.textContent = label;
    chip.appendChild(text);

    const button = document.createElement("button");
    button.type = "button";
    button.className = "ml-1";
    button.textContent = "×";
    button.dataset.action = "click->menu-target-picker#remove";
    button.dataset.menuTargetPickerTokenParam = token;
    chip.appendChild(button);

    return chip;
  }

  _removeToken(token) {
    this.hiddenInputsTarget
      .querySelectorAll(`input[data-token="${token}"]`)
      .forEach((el) => el.remove());
    this.chipsTarget
      .querySelectorAll(`span[data-token="${token}"]`)
      .forEach((el) => el.remove());
  }

  _run() {
    const params = new URLSearchParams();
    params.set("query", this.hasInputTarget ? this.inputTarget.value : "");
    params.set("scope", this._scope());
    this._selectedTokens().forEach((token) =>
      params.append("selected[]", token),
    );

    get(`${this.urlValue}?${params.toString()}`, {
      responseKind: "turbo-stream",
    })
      .then((response) => {
        if (!response.ok) this._toastError();
      })
      .catch((error) => {
        this._toastError();
        Sentry.captureException(error);
      });
  }

  _toastError() {
    new ToastService().toast({
      text: this.errorMessageValue,
      type: "error",
    });
  }
}
